import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import dayjs from "dayjs";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import {
  QuickbooksTimeExportItemStatus,
  useGetQuickbooksTimeExportRunItemsQuery,
  useGetQuickbooksTimeExportRunQuery,
  useRetryQuickbooksTimeExportRunFailedItemsMutation,
} from "generated/graphql";
import useGlobalMessage from "hooks/notifications/useGlobalMessage";
import { useState } from "react";
import EditTimeEntryModal from "modules/TimeEntries/EditTimeEntryModal";

type Props = {
  open: boolean;
  runId: string | null;
  isStarting?: boolean;
  onClose: () => void;
};

const TERMINAL_STATUSES = ["COMPLETED", "COMPLETED_WITH_ERRORS", "FAILED"];

const getStatusColor = (status?: string): "success" | "error" | "warning" | "info" => {
  if (!status) {
    return "info";
  }

  if (status === "COMPLETED") {
    return "success";
  }

  if (status === "COMPLETED_WITH_ERRORS" || status === "FAILED") {
    return "error";
  }

  if (status === "RUNNING" || status === "QUEUED") {
    return "warning";
  }

  return "info";
};

export default function QuickbooksTimeExportResultsModal({
  open,
  runId,
  isStarting = false,
  onClose,
}: Props) {
  const { show } = useGlobalMessage();
  const [selectedEntry, setSelectedEntry] = useState<any | null>(null);

  const {
    data: runData,
    loading: runLoading,
    refetch: refetchRun,
  } = useGetQuickbooksTimeExportRunQuery({
    variables: { id: runId ?? "" },
    skip: !open || !runId,
    fetchPolicy: "network-only",
    pollInterval: 3000,
  });

  const run = runData?.getQuickbooksTimeExportRun;
  const isTerminal = run ? TERMINAL_STATUSES.includes(run.status) : false;

  const {
    data: failedItemsData,
    loading: failedItemsLoading,
    refetch: refetchFailedItems,
  } = useGetQuickbooksTimeExportRunItemsQuery({
    variables: {
      runId: runId ?? "",
      first: 100,
      page: 1,
      status: QuickbooksTimeExportItemStatus.FAILED,
    },
    skip: !open || !runId,
    fetchPolicy: "network-only",
    pollInterval: isTerminal ? 0 : 3000,
  });

  const [retryFailedItems, { loading: retrying }] =
    useRetryQuickbooksTimeExportRunFailedItemsMutation(
    {
      onCompleted: () => {
        show({
          message: "Retrying failed QuickBooks export rows.",
          options: { variant: "success" },
        });
        refetchRun();
        refetchFailedItems();
      },
      onError: (error) => {
        show({
          message: error.message,
          options: { variant: "error" },
        });
      },
    }
  );

  const failedItems = failedItemsData?.getQuickbooksTimeExportRunItems?.data ?? [];
  const progressPercent = run?.totalItems
    ? Math.round(((run.processedItems || 0) / run.totalItems) * 100)
    : 0;
  const statusColor = getStatusColor(run?.status);
  const isSyncActive =
    isStarting || retrying || !run || runLoading || failedItemsLoading || !isTerminal;

  const handleRetryFailed = () => {
    if (!runId) {
      return;
    }

    retryFailedItems({ variables: { runId } });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
          <MDTypography variant="h5">QuickBooks Online Export</MDTypography>
          {run?.status && (
            <Chip label={run.status.replaceAll("_", " ")} color={statusColor} size="small" />
          )}
        </Box>
      </DialogTitle>
      <DialogContent>
        {runLoading && (
          <Box display="flex" alignItems="center" gap={1} py={2}>
            <CircularProgress size={18} />
            <MDTypography variant="button">Loading export status...</MDTypography>
          </Box>
        )}

        {isStarting && (
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              mb: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1.25,
              borderColor: "info.main",
              backgroundColor: "info.50",
            }}
          >
            <CircularProgress size={20} />
            <MDTypography variant="button" fontWeight="medium">
              Starting QuickBooks sync...
            </MDTypography>
          </Paper>
        )}

        {!runLoading && run && (
          <>
            {isSyncActive && (
              <Paper
                variant="outlined"
                sx={{
                  p: 1.25,
                  mb: 2,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  borderColor: "info.main",
                  backgroundColor: "info.50",
                }}
              >
                <CircularProgress size={16} />
                <MDTypography variant="button" fontWeight="medium">
                  Sync in progress. Processing {run?.processedItems ?? 0} of {run?.totalItems ?? 0}{" "}
                  entries...
                </MDTypography>
              </Paper>
            )}

            {isSyncActive && <LinearProgress sx={{ mb: 2 }} />}

            <Box display="grid" gridTemplateColumns="repeat(5, minmax(0, 1fr))" gap={1.25} mb={2}>
              <Paper variant="outlined" sx={{ p: 1.25 }}>
                <MDTypography variant="caption" color="text">
                  Total
                </MDTypography>
                <MDTypography variant="h6">{run.totalItems}</MDTypography>
              </Paper>
              <Paper variant="outlined" sx={{ p: 1.25 }}>
                <MDTypography variant="caption" color="text">
                  Exported
                </MDTypography>
                <MDTypography variant="h6" color="success">
                  {run.succeededItems}
                </MDTypography>
              </Paper>
              <Paper variant="outlined" sx={{ p: 1.25 }}>
                <MDTypography variant="caption" color="text">
                  Failed
                </MDTypography>
                <MDTypography variant="h6" color="error">
                  {run.failedItems}
                </MDTypography>
              </Paper>
              <Paper variant="outlined" sx={{ p: 1.25 }}>
                <MDTypography variant="caption" color="text">
                  Skipped
                </MDTypography>
                <MDTypography variant="h6">{run.skippedItems}</MDTypography>
              </Paper>
              <Paper variant="outlined" sx={{ p: 1.25 }}>
                <MDTypography variant="caption" color="text">
                  Processed
                </MDTypography>
                <MDTypography variant="h6">{run.processedItems}</MDTypography>
              </Paper>
            </Box>

            <Box mb={2}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                <MDTypography variant="caption" color="text">
                  {run.dateStart} to {run.dateEnd}
                </MDTypography>
                <MDTypography variant="caption" color="text">
                  {progressPercent}%
                </MDTypography>
              </Box>
              <LinearProgress variant="determinate" value={progressPercent} color={statusColor} />
            </Box>

            {run.lastErrorMessage && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {run.lastErrorCode ? `${run.lastErrorCode}: ` : ""}
                {run.lastErrorMessage}
              </Alert>
            )}

            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <MDTypography variant="button" fontWeight="medium">
                Failed Rows (
                {failedItemsData?.getQuickbooksTimeExportRunItems?.paginatorInfo?.total ?? 0})
              </MDTypography>
              <MDButton
                variant="contained"
                onClick={handleRetryFailed}
                disabled={retrying || !run.failedItems}
                sx={{
                  backgroundColor: "#6c757d",
                  color: "#ffffff",
                  minHeight: "32px",
                  px: 1.75,
                  py: 0.4,
                  "&:hover": {
                    backgroundColor: "#5a6268",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "#adb5bd",
                    color: "#ffffff",
                  },
                }}
              >
                Retry Failed Only
              </MDButton>
            </Box>

            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{ maxHeight: 360, width: "100%" }}
            >
              <Table
                size="small"
                stickyHeader
                sx={{
                  width: "100%",
                  minWidth: "100%",
                  "& .MuiTableHead-root": {
                    display: "table-header-group !important",
                    padding: "0 !important",
                  },
                  "& .MuiTableBody-root": {
                    display: "table-row-group !important",
                  },
                  "& .MuiTableRow-root": {
                    display: "table-row !important",
                  },
                  "& .MuiTableCell-root": {
                    display: "table-cell !important",
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>User</TableCell>
                    <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                      Customer / Job
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>Job Phase</TableCell>
                    <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>Error</TableCell>
                    <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {failedItemsLoading && (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <MDTypography variant="caption">Loading failed rows...</MDTypography>
                      </TableCell>
                    </TableRow>
                  )}

                  {!failedItemsLoading && failedItems.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <MDTypography variant="caption">No failed rows.</MDTypography>
                      </TableCell>
                    </TableRow>
                  )}

                  {!failedItemsLoading &&
                    failedItems.map((item: any) => {
                      const customerName = item?.timeEntry?.jobPhase?.job?.company?.name || "-";
                      const jobId = item?.timeEntry?.jobPhase?.job?.externalId || "-";
                      const phaseName = item?.timeEntry?.jobPhase?.proposalStage?.name || "-";
                      const userName = item?.timeEntry?.user?.name || "-";
                      const entryDate = item?.timeEntry?.forDate
                        ? dayjs(item.timeEntry.forDate).format("YYYY-MM-DD")
                        : "-";

                      return (
                        <TableRow
                          key={item.id}
                          sx={{
                            cursor: item?.timeEntry?.uuid ? "pointer" : "default",
                            "&:hover": {
                              backgroundColor: item?.timeEntry?.uuid
                                ? "rgba(17, 205, 239, 0.08)"
                                : "inherit",
                            },
                          }}
                          onClick={() => {
                            if (!item?.timeEntry?.uuid) {
                              return;
                            }
                            setSelectedEntry(item.timeEntry);
                          }}
                        >
                          <TableCell>{userName}</TableCell>
                          <TableCell>{entryDate}</TableCell>
                          <TableCell>{`${customerName} / ${jobId}`}</TableCell>
                          <TableCell>{phaseName}</TableCell>
                          <TableCell>
                            <MDTypography
                              variant="caption"
                              color="error"
                              sx={{ whiteSpace: "normal" }}
                            >
                              {item.errorMessage || item.errorCode || "Unknown error"}
                            </MDTypography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={item.recommendedAction || "RETRY"}
                              color="warning"
                              variant="outlined"
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>

            {isTerminal && run.finishedAt && (
              <MDTypography variant="caption" color="text" mt={1} display="block">
                Completed {dayjs(run.finishedAt).format("YYYY-MM-DD hh:mm A")}
              </MDTypography>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      {selectedEntry && (
        <EditTimeEntryModal
          open={Boolean(selectedEntry)}
          entry={selectedEntry}
          onClose={() => {
            setSelectedEntry(null);
            refetchRun();
            refetchFailedItems();
          }}
        />
      )}
    </Dialog>
  );
}
